import { useState, useMemo } from "react";
import { Navigate } from "react-router";
import { Search, Loader2, Shield, ShieldOff, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  useUserProfiles,
  useUserRoles,
  useGrantRole,
  useRevokeRole,
  useStaffAtDailyPatientLimit,
  useResetDailyPatientLimit,
} from "@/hooks/api";
import { getCurrentAuthUserId } from "@/lib/current-user";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table";

const ROLES = ["admin", "bht", "crm", "doctor", "superuser"] as const;
type Role = (typeof ROLES)[number];

export function AuthorizationPage() {
  const { hasRole } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  if (!hasRole("superuser")) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Autorisering</h2>
      <DailyPatientLimitPanel />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <UserPanel
          selectedUserId={selectedUserId}
          onSelect={setSelectedUserId}
        />
        <CommandPanel userId={selectedUserId} />
      </div>
    </div>
  );
}

// ── User panel: search + select + view roles ───────────────────────────

function UserPanel({
  selectedUserId,
  onSelect,
}: {
  selectedUserId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const [search, setSearch] = useState("");
  const { data: profiles, isLoading } = useUserProfiles();

  const filtered = useMemo(() => {
    if (!profiles || !search.trim()) return [];
    const q = search.toLowerCase();
    return profiles
      .filter(
        (p) =>
          p.givenName?.toLowerCase().includes(q) ||
          p.familyName?.toLowerCase().includes(q) ||
          p.email?.toLowerCase().includes(q) ||
          p.authUserId?.toLowerCase().includes(q)
      )
      .slice(0, 30);
  }, [profiles, search]);

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <h3 className="text-sm font-semibold text-admin-text-secondary uppercase tracking-wider">
          Velg bruker
        </h3>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-admin-text-subtle" />
          <Input
            placeholder="Sok etter navn, e-post eller ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-admin-text-subtle">
            <Loader2 className="h-4 w-4 animate-spin" />
            Laster brukere...
          </div>
        )}

        {search.trim() && filtered.length > 0 && (
          <div className="max-h-64 overflow-y-auto rounded-md border border-admin-card-border">
            <Table>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow
                    key={p.authUserId}
                    className={`cursor-pointer ${selectedUserId === p.authUserId ? "bg-admin-primary/10" : "hover:bg-admin-slate-hover"}`}
                    onClick={() => {
                      onSelect(p.authUserId);
                      setSearch("");
                    }}
                  >
                    <TableCell className="py-2">
                      <div className="font-medium text-sm">
                        {p.givenName} {p.familyName}
                      </div>
                      <div className="text-xs text-admin-text-subtle">
                        {p.email}
                      </div>
                    </TableCell>
                    <TableCell className="py-2 text-right">
                      <code className="text-xs text-admin-text-subtle">
                        {p.authUserId.slice(0, 8)}...
                      </code>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {search.trim() && filtered.length === 0 && !isLoading && (
          <p className="text-sm text-admin-text-subtle">Ingen treff</p>
        )}

        {selectedUserId && (
          <SelectedUserRoles
            userId={selectedUserId}
            onClear={() => onSelect(null)}
          />
        )}
      </CardContent>
    </Card>
  );
}

function SelectedUserRoles({
  userId,
  onClear,
}: {
  userId: string;
  onClear: () => void;
}) {
  const { data: roles, isLoading } = useUserRoles(userId);
  const { data: profiles } = useUserProfiles();
  const profile = profiles?.find((p) => p.authUserId === userId);

  return (
    <div className="space-y-3 pt-4 border-t border-admin-card-border">
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium text-sm">
            {profile
              ? `${profile.givenName} ${profile.familyName}`
              : userId.slice(0, 12) + "..."}
          </span>
          <code className="ml-2 text-xs text-admin-text-subtle">{userId}</code>
        </div>
        <Button variant="ghost" size="sm" onClick={onClear}>
          Fjern
        </Button>
      </div>

      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <div className="flex flex-wrap gap-2">
          {!roles?.length && (
            <span className="text-sm text-admin-text-subtle">
              Ingen roller
            </span>
          )}
          {roles?.map((r) => (
            <Badge
              key={r.id}
              variant={r.status === "active" ? "default" : "secondary"}
            >
              {r.role} ({r.status})
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Daily patient limit: staff who reached it, reset by someone else ──

function DailyPatientLimitPanel() {
  const { data: limited } = useStaffAtDailyPatientLimit();
  const { data: profiles } = useUserProfiles();
  if (!limited?.length) return null;

  const me = getCurrentAuthUserId();
  const nameOf = (id: string) => {
    const p = profiles?.find((p) => p.authUserId === id);
    return p ? `${p.givenName} ${p.familyName}` : id.slice(0, 12) + "...";
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <h3 className="text-sm font-semibold text-admin-text-secondary uppercase tracking-wider">
          Nådd daglig pasientgrense
        </h3>
        <Table>
          <TableBody>
            {limited.map((s) => (
              <LimitedStaffRow
                key={s.staffUserId}
                staffUserId={s.staffUserId}
                name={nameOf(s.staffUserId)}
                opened={s.opened}
                isSelf={s.staffUserId === me}
              />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function LimitedStaffRow({
  staffUserId,
  name,
  opened,
  isSelf,
}: {
  staffUserId: string;
  name: string;
  opened: number;
  isSelf: boolean;
}) {
  const [reason, setReason] = useState("");
  const resetLimit = useResetDailyPatientLimit();
  const reasonId = `reset-reason-${staffUserId}`;

  const handleReset = async () => {
    if (!reason.trim()) {
      toast.error("Begrunnelse er påkrevd");
      return;
    }
    try {
      await resetLimit.mutateAsync({ staffUserId, reason });
      toast.success(`Grensen er nullstilt for ${name}`);
    } catch (err: any) {
      toast.error(err.message ?? "Kunne ikke nullstille grensen");
    }
  };

  return (
    <TableRow>
      <TableCell className="py-2 align-bottom">
        <div className="font-medium text-sm">{name}</div>
        <div className="text-xs text-admin-text-subtle">
          {opened} pasienter åpnet
        </div>
      </TableCell>
      <TableCell className="py-2">
        {isSelf ? (
          <span className="text-sm text-admin-text-subtle">
            En annen administrator må nullstille din grense
          </span>
        ) : (
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label
                htmlFor={reasonId}
                className="text-xs font-medium text-admin-text-secondary"
              >
                Begrunnelse *
              </label>
              <Input
                id={reasonId}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1"
              />
            </div>
            <Button onClick={handleReset} disabled={resetLimit.isPending}>
              {resetLimit.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              Nullstill
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

// ── Command panel: grant / revoke ──────────────────────────────────────

function CommandPanel({ userId }: { userId: string | null }) {
  const [command, setCommand] = useState<"grant_role" | "revoke_role">(
    "grant_role"
  );

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <h3 className="text-sm font-semibold text-admin-text-secondary uppercase tracking-wider">
          Kjor kommando
        </h3>

        <div className="flex gap-2">
          <Button
            variant={command === "grant_role" ? "default" : "outline"}
            size="sm"
            onClick={() => setCommand("grant_role")}
          >
            <Shield className="h-4 w-4 mr-1" />
            Gi rolle
          </Button>
          <Button
            variant={command === "revoke_role" ? "default" : "outline"}
            size="sm"
            onClick={() => setCommand("revoke_role")}
          >
            <ShieldOff className="h-4 w-4 mr-1" />
            Fjern rolle
          </Button>
        </div>

        {!userId && (
          <p className="text-sm text-admin-text-subtle">
            Velg en bruker forst.
          </p>
        )}

        {userId && command === "grant_role" && (
          <GrantRoleForm userId={userId} />
        )}
        {userId && command === "revoke_role" && (
          <RevokeRoleForm userId={userId} />
        )}
      </CardContent>
    </Card>
  );
}

function GrantRoleForm({ userId }: { userId: string }) {
  const [role, setRole] = useState<Role>("admin");
  const [purpose, setPurpose] = useState("");
  const [organization, setOrganization] = useState("");
  const grantRole = useGrantRole();

  const handleSubmit = async () => {
    if (!purpose.trim()) {
      toast.error("Formal er pakrevd");
      return;
    }
    try {
      await grantRole.mutateAsync({
        authUserId: userId,
        role,
        grantReason: purpose,
        ...(organization.trim() ? { organization } : {}),
      });
      toast.success(`Rolle "${role}" gitt`);
      setPurpose("");
      setOrganization("");
    } catch (err: any) {
      toast.error(err.message ?? "Feil ved tildeling av rolle");
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-medium text-admin-text-secondary">
          Rolle
        </label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          className="mt-1 w-full rounded-md border border-admin-card-border bg-admin-bg px-3 py-2 text-sm"
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-admin-text-secondary">
          Formal *
        </label>
        <Input
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          placeholder="Hvorfor gis denne rollen?"
          className="mt-1"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-admin-text-secondary">
          Organisasjon
        </label>
        <Input
          value={organization}
          onChange={(e) => setOrganization(e.target.value)}
          placeholder="Valgfri"
          className="mt-1"
        />
      </div>
      <Button
        onClick={handleSubmit}
        disabled={grantRole.isPending}
        className="w-full"
      >
        {grantRole.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : (
          <Shield className="h-4 w-4 mr-2" />
        )}
        Gi rolle
      </Button>
    </div>
  );
}

function RevokeRoleForm({ userId }: { userId: string }) {
  const { data: roles } = useUserRoles(userId);
  const activeRoles = useMemo(
    () => roles?.filter((r) => r.status === "active") ?? [],
    [roles]
  );
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const revokeRole = useRevokeRole();

  const currentRoleId = selectedRoleId || activeRoles[0]?.id || "";
  const currentRole = activeRoles.find((r) => r.id === currentRoleId);

  const handleSubmit = async () => {
    if (!currentRoleId) {
      toast.error("Ingen aktive roller a fjerne");
      return;
    }
    try {
      await revokeRole.mutateAsync({
        roleId: currentRoleId,
        authUserId: userId,
      });
      toast.success(`Rolle "${currentRole?.role}" fjernet`);
      setSelectedRoleId("");
    } catch (err: any) {
      toast.error(err.message ?? "Feil ved fjerning av rolle");
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-medium text-admin-text-secondary">
          Rolle
        </label>
        {activeRoles.length === 0 ? (
          <p className="mt-1 text-sm text-admin-text-subtle">
            Ingen aktive roller
          </p>
        ) : (
          <select
            value={currentRoleId}
            onChange={(e) => setSelectedRoleId(e.target.value)}
            className="mt-1 w-full rounded-md border border-admin-card-border bg-admin-bg px-3 py-2 text-sm"
          >
            {activeRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.role}
              </option>
            ))}
          </select>
        )}
      </div>
      <Button
        onClick={handleSubmit}
        disabled={revokeRole.isPending || activeRoles.length === 0}
        variant="destructive"
        className="w-full"
      >
        {revokeRole.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : (
          <ShieldOff className="h-4 w-4 mr-2" />
        )}
        Fjern rolle
      </Button>
    </div>
  );
}
